'use client'

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { UserContextProvider } from '@/lib/contexts/UserContext'
import { FeedbackDialog } from '@/components/feedback-dialog'
import { EventEmitterProvider, useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import { useLocalStorageState } from 'ahooks'
import { getBugsnagClient } from '@/lib/bugsnag/bugsnag'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'

function onQueryClientError(error: Error) {
  console.error(error)
  getBugsnagClient()?.notify(error)
  toast.error('An error occurred. Please try again later.')
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        // Don't refetch on window focus
        refetchOnWindowFocus: false,
        // Do not retry on error
        retry: false,
      },
      mutations: {
        // Do not retry on error
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: onQueryClientError,
    }),
    mutationCache: new MutationCache({
      onError: onQueryClientError,
    }),
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}

// This is a bit of a hack, but we need to start Bugsnag
function startBugsnag() {
  getBugsnagClient()
}

/**
 * Feedback dialog handler.
 * This component listens to app-wide events and shows the feedback dialog when needed.
 * Also, when query param feedback=new-design is present, it will show the feedback dialog.
 */
function FeedbackDialogHandler() {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const appEvent$ = useEventEmitterContext()
  const [lastDownloadedAt, setLastDownloadedAt] = useLocalStorageState('crate.lastDownloadedAt')

  appEvent$.useSubscription((evt) => {
    if (evt.type === 'asset:download') {
      //do not display the feedback dialog after download anynmore as requested by the client
      //if (!lastDownloadedAt) {
      //  setTimeout(() => setFeedbackDialogOpen(true), 3000)
      //}
      setLastDownloadedAt(Date.now())
    }
  })

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('feedback') === 'new-design') {
      setFeedbackDialogOpen(true)
    }
  }, [])

  return <FeedbackDialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen} />
}

export default function Providers({ children }: { children: React.ReactNode }) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient()

  // Start Bugsnag on mount
  useEffect(() => {
    startBugsnag()
  }, [])

  return (
    <>
      <UserContextProvider>
        <QueryClientProvider client={queryClient}>
          <EventEmitterProvider>
            {children}
            <FeedbackDialogHandler />
          </EventEmitterProvider>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </QueryClientProvider>
      </UserContextProvider>
      <Toaster richColors position="bottom-center" />
    </>
  )
}
